/* ============================================================
   ProfileDrawer — CB-1 (0138037b): customer profile self-service.
   Right-side drawer opened from the Navbar. Reads GET /api/v1/me,
   edits DisplayName + PhoneNumber (PUT /api/v1/me) and changes the
   password (POST /api/v1/me/password). Email/City/Role are shown
   read-only. On a successful save the local session displayName is
   patched so the Navbar updates without a re-login.
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { loadAuth, saveAuth } from '../auth/tokenStore';
import { ROLE_LABELS, type Profile } from '../types/auth';
import { AddressBookSection } from './AddressBookSection';
import {
  buildPasswordSubmit,
  buildProfileDraft,
  buildUpdateRequest,
  canSaveProfile,
  displayNameError,
  isProfileDirty,
  parsePhoneInput,
  profileHeaderView,
  type ProfileDraft,
} from './profileView';

interface Props {
  open: boolean;
  onClose: () => void;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 'var(--fs-body)',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--color-separator)',
  background: 'var(--color-surface)',
};

const sectionTitle: React.CSSProperties = {
  fontSize: 'var(--fs-h3)',
  fontWeight: 'var(--fw-bold)',
  color: 'var(--color-text-primary)',
};

const muted: React.CSSProperties = {
  fontSize: 'var(--fs-caption)',
  color: 'var(--color-text-secondary)',
};

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ ...muted, fontWeight: 'var(--fw-semibold)' }}>{label}</span>
      {children}
    </label>
  );
}

function ReadOnlyRow({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 'var(--space-md)' }}>
      <span style={muted}>{label}</span>
      <span
        style={{
          fontSize: 'var(--fs-caption)',
          fontWeight: 'var(--fw-medium)',
          color: 'var(--color-text-primary)',
          textAlign: 'right',
          overflowWrap: 'anywhere',
        }}
      >
        {value}
      </span>
    </div>
  );
}

export function ProfileDrawer({ open, onClose }: Props) {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [draft, setDraft] = useState<ProfileDraft | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [savedCue, setSavedCue] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordCue, setPasswordCue] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  // (Re)load the profile every time the drawer opens.
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setLoadError(null);
    setSavedCue(false);
    setSaveError(null);
    setPasswordCue(false);
    setPasswordError(null);
    setCurrentPassword('');
    setNewPassword('');
    setRepeatPassword('');
    api
      .getProfile()
      .then((p) => {
        setProfile(p);
        setDraft(buildProfileDraft(p));
      })
      .catch((err) => setLoadError(err instanceof Error ? err.message : 'Не удалось загрузить профиль'))
      .finally(() => setLoading(false));
  }, [open]);

  // Close on Escape.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const handleProfileSave = useCallback(async () => {
    if (!profile || !draft) return;
    const phone = parsePhoneInput(draft.phoneNumber);
    if (!phone.ok) {
      setSaveError(phone.error);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setSavedCue(false);
    try {
      const request = buildUpdateRequest(draft, profile);
      if (request.phoneNumber !== undefined) request.phoneNumber = phone.phone;
      const updated = await api.updateProfile(request);
      setProfile(updated);
      setDraft(buildProfileDraft(updated));
      setSavedCue(true);
      // Patch the local session so the Navbar shows the fresh name.
      const session = loadAuth();
      if (session && session.displayName !== updated.displayName) {
        saveAuth({ ...session, displayName: updated.displayName });
      }
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Не удалось сохранить профиль');
    } finally {
      setSaving(false);
    }
  }, [profile, draft]);

  const handlePasswordChange = useCallback(async () => {
    const submit = buildPasswordSubmit(currentPassword, newPassword, repeatPassword);
    if (!submit.ok) {
      setPasswordError(submit.error);
      return;
    }
    setChangingPassword(true);
    setPasswordError(null);
    setPasswordCue(false);
    try {
      await api.changePassword(submit.request);
      setPasswordCue(true);
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    } catch (err) {
      setPasswordError(err instanceof Error ? err.message : 'Не удалось изменить пароль');
    } finally {
      setChangingPassword(false);
    }
  }, [currentPassword, newPassword, repeatPassword]);

  if (!open) return null;

  const nameError = draft ? displayNameError(draft.displayName) : null;
  const phoneParsed = draft ? parsePhoneInput(draft.phoneNumber) : null;
  const phoneError =
    phoneParsed && !phoneParsed.ok && isProfileDirty(draft!, profile!) && draft!.phoneNumber.trim() !== (profile!.phoneNumber ?? '')
      ? phoneParsed.error
      : null;
  const header = profile ? profileHeaderView(profile, ROLE_LABELS[profile.role] ?? profile.role) : null;

  return (
    <div
      role="dialog"
      aria-label="Профиль"
      style={{ position: 'fixed', inset: 0, zIndex: 200, display: 'flex', justifyContent: 'flex-end' }}
    >
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{ position: 'absolute', inset: 0, background: 'rgba(0, 0, 0, 0.28)' }}
      />

      {/* Panel */}
      <aside
        style={{
          position: 'relative',
          width: 'min(420px, 100vw)',
          height: '100%',
          background: 'var(--color-surface)',
          borderLeft: '1px solid var(--color-separator)',
          padding: 'var(--space-lg) var(--space-lg)',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--space-lg)',
          boxShadow: 'none',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={sectionTitle}>Профиль</h2>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              fontSize: 'var(--fs-body)',
              color: 'var(--color-text-secondary)',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        </div>

        {loading && <p style={muted}>Загрузка профиля…</p>}
        {loadError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{loadError}</p>}

        {profile && draft && header && (
          <>
            {/* Read-only identity */}
            <div
              className="card"
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)', padding: 'var(--space-md)' }}
            >
              <ReadOnlyRow label="Email" value={profile.email} />
              <ReadOnlyRow label="Роль" value={header.roleLine} />
              <ReadOnlyRow label="Город" value={profile.city} />
              <ReadOnlyRow label={header.balanceLine} value="" />
            </div>

            {/* Editable fields */}
            <form
              style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}
              onSubmit={(e) => {
                e.preventDefault();
                void handleProfileSave();
              }}
            >
              <Field label="Имя">
                <input
                  type="text"
                  value={draft.displayName}
                  onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
                  placeholder="Как вас называть"
                  style={inputStyle}
                />
              </Field>
              {nameError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{nameError}</p>}

              <Field label="Телефон">
                <input
                  type="tel"
                  value={draft.phoneNumber}
                  onChange={(e) => setDraft({ ...draft, phoneNumber: e.target.value })}
                  placeholder="+7 999 123-45-67 (необязательно)"
                  style={inputStyle}
                />
              </Field>
              {phoneError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{phoneError}</p>}

              {saveError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{saveError}</p>}
              {savedCue && <p style={{ ...muted, color: 'var(--color-success)' }}>Профиль сохранён</p>}

              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving || !canSaveProfile(draft, profile)}
              >
                {saving ? 'Сохранение…' : 'Сохранить'}
              </button>
            </form>

            {/* Password change */}
            <form
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-md)',
                borderTop: '1px solid var(--color-separator)',
                paddingTop: 'var(--space-lg)',
              }}
              onSubmit={(e) => {
                e.preventDefault();
                void handlePasswordChange();
              }}
            >
              <div>
                <p style={{ ...sectionTitle, fontSize: 'var(--fs-body)' }}>Смена пароля</p>
                <p style={{ ...muted, marginTop: 2 }}>Текущие сессии останутся активными.</p>
              </div>

              <Field label="Текущий пароль">
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  style={inputStyle}
                />
              </Field>
              <Field label="Новый пароль">
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </Field>
              <Field label="Повторите новый пароль">
                <input
                  type="password"
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  autoComplete="new-password"
                  style={inputStyle}
                />
              </Field>

              {passwordError && <p style={{ ...muted, color: 'var(--color-danger)' }}>{passwordError}</p>}
              {passwordCue && <p style={{ ...muted, color: 'var(--color-success)' }}>Пароль обновлён</p>}

              <button
                type="submit"
                className="btn btn-secondary"
                disabled={
                  changingPassword || currentPassword.length === 0 || newPassword.length === 0 || repeatPassword.length === 0
                }
              >
                {changingPassword ? 'Сохранение…' : 'Изменить пароль'}
              </button>
            </form>

            {/* CB-3: address book management */}
            <AddressBookSection />
          </>
        )}
      </aside>
    </div>
  );
}
